import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WhatsappLogo, ArrowLeft, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(data?.message || "E-mail enviado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar e-mail. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[#0F4C3A] text-white flex items-center justify-center">
            <WhatsappLogo size={20} weight="bold" />
          </div>
          <span className="font-bold tracking-tight text-lg">NETSZAP</span>
        </div>

        <div className="bg-white border-2 border-[#E5E5E0] p-8">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-sm text-[#5C5C59] hover:text-[#0F4C3A] mb-6"
            data-testid="fp-back"
          >
            <ArrowLeft size={16} weight="bold" />
            Voltar para o login
          </button>

          {!sent ? (
            <>
              <h1 className="text-2xl font-black tracking-tighter mb-2">Esqueceu sua senha?</h1>
              <p className="text-sm text-[#5C5C59] mb-6">
                Sem problema. Digite o e-mail cadastrado que a gente te envia um link para criar uma nova senha.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest">E-mail</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="voce@empresa.com"
                    data-testid="fp-email"
                    className="rounded-none mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white h-11"
                  data-testid="fp-submit"
                >
                  {submitting ? "Enviando…" : "Enviar link de recuperação"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-[#0F4C3A] text-white flex items-center justify-center rounded-full mx-auto mb-5">
                <EnvelopeSimple size={32} weight="fill" />
              </div>
              <h1 className="text-2xl font-black tracking-tighter mb-2">Confira seu e-mail!</h1>
              <p className="text-sm text-[#5C5C59] mb-6 leading-relaxed">
                Se o e-mail <b className="text-black">{email}</b> estiver cadastrado, você receberá um link para redefinir sua senha em instantes.
              </p>
              <div className="bg-[#F7F7F5] border border-[#E5E5E0] p-4 text-left text-xs text-[#5C5C59] space-y-1.5">
                <p className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#0F4C3A] mt-0.5 flex-shrink-0" />
                  <span>O link expira em <b>1 hora</b></span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#0F4C3A] mt-0.5 flex-shrink-0" />
                  <span>Não esqueça de checar a caixa de <b>spam</b></span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#0F4C3A] mt-0.5 flex-shrink-0" />
                  <span>Não recebeu? Tente enviar novamente em alguns minutos</span>
                </p>
              </div>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-sm text-[#0F4C3A] hover:underline mt-5"
                data-testid="fp-resend"
              >
                Enviar para outro e-mail
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[#5C5C59] mt-6">
          Precisa de ajuda? <Link to="/login" className="text-[#0F4C3A] hover:underline">Voltar ao login</Link>
        </p>
      </div>
    </div>
  );
}
